import { useEffect, useRef, useState } from 'react';
import { SearchResults } from '../../components/SearchResults';
import { PageHeader } from '../../components/PageHeader';
import { Breadcrumb } from '../../components/Breadcrumb';
import { useSearchStore } from '../../stores/searchStore';
import { useResourceSelectionStore } from '../../stores/resourceSelectionStore';
import { useProject } from '../../hooks/useProject';
import { useCollection } from '../../hooks/useCollections';
import {
  onAngularEvent,
  getCurrentProject,
  getCurrentCollection,
  pushSearchLayer,
} from '../../services/angular-bridge';

/** SearchContext.Collection === 0, SearchContext.Project === 1 (model/search/ISearch.ts). */
const CONTEXT_COLLECTION = 0;

/**
 * SearchResultsPage — the React half of SearchController + views/search_results.html.
 *
 * Primarily mounted in the search *layer* (index.html), shown and hidden by the Angular
 * nav search input, which also still owns the query itself. So the page ignores route
 * params — it reads the current project/collection from the Angular services and then
 * tracks them, and the live search, through $rootScope events. The /search routes mount
 * it too, but like SearchController before it, it renders an empty shell there until a
 * search is executed.
 */
export function SearchResultsPage() {
  const search = useSearchStore((s) => s.search);
  const collectionId = useSearchStore((s) => s.collectionId);
  const setSearch = useSearchStore((s) => s.setSearch);
  const setCollectionId = useSearchStore((s) => s.setCollectionId);
  const clearSelection = useResourceSelectionStore((s) => s.clear);

  const [projectId, setProjectId] = useState<string>(() => getCurrentProject()?.id ?? '');
  // Search.SearchExecuted coalescing (see the handler below).
  const pendingRef = useRef<{ advanced: boolean; expression: unknown } | null>(null);
  const flushScheduled = useRef(false);

  useEffect(() => {
    // Whatever the collection scope is when we mount; SearchContext.Changed keeps it current.
    setCollectionId(getCurrentCollection()?.id ?? null);

    const unsubscribes = [
      // Both the nav input (basic) and the expression builder (advanced) broadcast this.
      // One user action can raise several of these in a single digest: the advanced
      // builder recurses, so every live expression-group re-broadcasts it. Each one would
      // otherwise store a fresh `search` object, and the grid purges and refetches on every
      // datasource identity change — the results would blink away as they arrived. Coalesce
      // per tick (latest wins) rather than dropping repeats: a genuinely re-run search
      // arrives in a later tick and must still refetch, since nothing here is cached.
      onAngularEvent('Search.SearchExecuted', (args: { advanced: boolean; expression: unknown }) => {
        pendingRef.current = args;
        if (flushScheduled.current) return;
        flushScheduled.current = true;
        queueMicrotask(() => {
          flushScheduled.current = false;
          const latest = pendingRef.current;
          pendingRef.current = null;
          if (!latest) return;
          setSearch(
            latest.advanced
              ? { advanced: true, expression: latest.expression }
              : { advanced: false, query: String(latest.expression ?? '') }
          );
          // SearchController:100 did this. Without it, entry points that don't raise the
          // layer themselves (saved searches, advanced builder, voice) execute a search
          // into a layer that never becomes visible.
          pushSearchLayer();
          // Layers share the window's scroll, so results would open at the main page's offset.
          window.scrollTo(0, 0);
        });
      }),
      // Scope dropdown. Pure state — the re-execute is the datasource rebuilding off the
      // new collection id, so Search.ContextChangedByUser needs no separate handler.
      onAngularEvent('SearchContext.Changed', (context: number) =>
        setCollectionId(context === CONTEXT_COLLECTION ? getCurrentCollection()?.id ?? null : null)
      ),
      // Collection-to-collection moves keep the context at Collection, so the event above
      // doesn't fire; this keeps the scope pointing at the collection actually current.
      onAngularEvent('CollectionService.CurrentCollectionChanged', (collection: { id: string } | null) => {
        if (collection) setCollectionId(collection.id);
      }),
      onAngularEvent('ProjectService.CurrentProjectChanged', (project: { id: string } | null) =>
        setProjectId(project?.id ?? '')
      ),
      // Leaving the search layer drops a selection that no longer has a grid behind it.
      onAngularEvent('LayerService.hide_search', () => clearSelection()),
    ];
    return () => unsubscribes.forEach((off) => off());
  }, [setSearch, setCollectionId, clearSelection]);

  const { data: project } = useProject(projectId);
  const { data: collection } = useCollection(projectId, collectionId ?? '');
  const scoped = collectionId ? collection : null;

  return (
    <div className="container-fluid" data-testid="search-results-panel">
      <PageHeader icon="fas fa-search" title={scoped?.name ?? project?.name ?? ''} />
      <Breadcrumb
        items={[
          { label: 'Projects', href: '/project' },
          ...(project ? [{ label: project.name, href: `/project/${projectId}/collections` }] : []),
          ...(scoped ? [{ label: scoped.name, href: `/project/${projectId}/collection/${scoped.id}` }] : []),
          { label: 'Search' },
        ]}
      />
      {search && projectId ? (
        <SearchResults projectId={projectId} collectionId={collectionId ?? undefined} search={search} testId="search-result-grid" />
      ) : (
        <p className="text-muted">Enter a search to see results.</p>
      )}
    </div>
  );
}
